/**
 * Caixa — o turno de HOJE: abrir, operar, fechar.
 *
 * # O que saiu desta tela
 *
 * O histórico de turnos, que agora é `/app/caixa/turnos`. São duas perguntas
 * diferentes: "quanto tem na gaveta agora" é operação, e "o turno de terça
 * bateu" é conferência. Uma listagem com filtro e paginação empilhada embaixo
 * do painel de operação não servia bem nenhuma das duas.
 *
 * Também saíram os indicadores, o extrato, a gaveta e os três diálogos, cada um
 * para o seu arquivo em `componentes/`. O que sobrou aqui é o que esta tela de
 * fato é: uma máquina de dois estados — com turno e sem turno — e as chamadas
 * que a levam de um para o outro.
 *
 * # Sem turno aberto não é erro
 *
 * `GET /caixa/turnos/atual` responde 200 com `turno: null` às oito da manhã de
 * toda segunda-feira. A tela mostra o convite para abrir, e não uma falha.
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { Botao } from '../../componentes/Botao'
import { Icone } from '../../componentes/Icone'
import { useSessao } from '../../plataforma/contexto-sessao'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { carregarTurnoAtual, type TurnoAtual } from '../../plataforma/caixa'
import { LayoutApp } from '../app/LayoutApp'
import { CartaoIndicador } from './componentes/CartaoIndicador'
import { DialogoAbertura } from './componentes/DialogoAbertura'
import { DialogoFechamento } from './componentes/DialogoFechamento'
import { DialogoMovimento } from './componentes/DialogoMovimento'
import { PainelGaveta } from './componentes/PainelGaveta'
import { TabelaMovimentos } from './componentes/TabelaMovimentos'

type Aberto = 'nenhum' | 'abertura' | 'sangria' | 'suprimento' | 'fechamento'

export function TelaCaixa() {
  const { pode } = useSessao()
  const [dados, setDados] = useState<TurnoAtual | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [falha, setFalha] = useState('')
  const [dialogo, setDialogo] = useState<Aberto>('nenhum')

  const recarregar = useCallback(async (sinal?: AbortSignal) => {
    try {
      const resposta = await carregarTurnoAtual(sinal)
      if (sinal?.aborted) return
      setDados(resposta)
      setFalha('')
    } catch {
      if (sinal?.aborted) return
      setFalha('Não foi possível carregar o caixa.')
    } finally {
      if (!sinal?.aborted) setCarregando(false)
    }
  }, [])

  useEffect(() => {
    const controle = new AbortController()
    void recarregar(controle.signal)
    return () => controle.abort()
  }, [recarregar])

  function concluir() {
    setDialogo('nenhum')
    setCarregando(true)
    void recarregar()
  }

  const turno = dados?.turno ?? null

  return (
    <LayoutApp
      titulo="Caixa"
      subtitulo={
        turno
          ? `${turno.terminal} · aberto às ${horaDe(turno.aberto_em)}${
              turno.aberto_por ? ` por ${turno.aberto_por}` : ''
            }`
          : 'Nenhum turno aberto'
      }
      acoes={
        <div className="flex items-center gap-2.5">
          <Link
            to="/app/caixa/turnos"
            className="flex h-[34px] items-center gap-2 rounded-sistema border border-borda bg-white px-4 text-[13.5px] font-semibold whitespace-nowrap text-marca"
          >
            <Icone nome="view_list" tamanho={18} />
            Histórico
          </Link>

          {turno ? (
            <Botao
                icone="lock"
                disabled={!pode('cash.shift')}
                onClick={() => setDialogo('fechamento')}
              >
                Fechar caixa
              </Botao>
          ) : (
            <Botao
                icone="payments"
                disabled={!pode('cash.shift')}
                onClick={() => setDialogo('abertura')}
              >
                Abrir caixa
              </Botao>
          )}
        </div>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {falha && (
          <div className="rounded-sistema border border-erro-barra bg-erro-fundo px-4 py-3 text-[13px] text-erro-forte">
            {falha}
          </div>
        )}

        {carregando && !dados ? (
          <p className="px-1 py-8 text-sm text-texto-suave">Carregando o caixa…</p>
        ) : turno ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              <CartaoIndicador
                rotulo="Dinheiro em gaveta"
                icone="payments"
                valor={turno.gaveta_centavos}
                detalhe="Abertura + vendas em espécie + suprimentos − sangrias"
              />
              <CartaoIndicador
                rotulo="Cartão e PIX"
                icone="point_of_sale"
                valor={turno.cartao_pix_centavos}
                detalhe="Não está na gaveta — está na adquirente"
              />
              <CartaoIndicador
                rotulo="Vendas do turno"
                icone="trending_up"
                valor={turno.vendas_total_centavos}
                detalhe={`${turno.quantidade_vendas} ${
                  turno.quantidade_vendas === 1 ? 'venda' : 'vendas'
                } desde a abertura`}
              />
              <CartaoIndicador
                rotulo="Sangrias e suprimentos"
                icone="trending_down"
                valor={turno.sangrias_centavos + turno.suprimentos_centavos}
                detalhe={`${formatarMoeda(turno.sangrias_centavos)} saíram · ${formatarMoeda(
                  turno.suprimentos_centavos
                )} entraram`}
              />
            </div>

            <div className="grid items-start gap-4 xl:grid-cols-[1.55fr_1fr]">
              <section className="min-w-0 rounded-sistema border border-borda bg-superficie p-5 shadow-[var(--shadow-cartao)]">
                <div className="mb-4">
                  <h2 className="font-titulo text-base font-semibold text-tinta">
                    Movimentos do turno
                  </h2>
                  <p className="mt-[3px] text-[12.5px] text-texto-suave">
                    Vendas em dinheiro, sangrias e suprimentos, em ordem
                  </p>
                </div>
                <TabelaMovimentos movimentos={dados?.movimentos ?? []} />
              </section>

              <div className="flex min-w-0 flex-col gap-4">
                <PainelGaveta
                  turno={turno}
                  podeMovimentar={pode('cash.movement')}
                  aoSangrar={() => setDialogo('sangria')}
                  aoSuprir={() => setDialogo('suprimento')}
                />
              </div>
            </div>
          </>
        ) : (
          <SemTurno podeAbrir={pode('cash.shift')} aoAbrir={() => setDialogo('abertura')} />
        )}
      </div>

      {dialogo === 'abertura' && (
        <DialogoAbertura aoFechar={() => setDialogo('nenhum')} aoAbrir={concluir} />
      )}

      {turno && (dialogo === 'sangria' || dialogo === 'suprimento') && (
        <DialogoMovimento
          tipo={dialogo}
          codigo={turno.codigo}
          gaveta={turno.gaveta_centavos}
          aoFechar={() => setDialogo('nenhum')}
          aoLancar={concluir}
        />
      )}

      {turno && dialogo === 'fechamento' && (
        <DialogoFechamento
          codigo={turno.codigo}
          esperado={turno.gaveta_centavos}
          aoFechar={() => setDialogo('nenhum')}
          aoConcluir={concluir}
        />
      )}
    </LayoutApp>
  )
}

/**
 * O estado sem turno. Não é vazio de erro: é o começo do dia.
 */
function SemTurno({ podeAbrir, aoAbrir }: { podeAbrir: boolean; aoAbrir: () => void }) {
  return (
    <section className="flex flex-col items-center gap-3 rounded-sistema border border-borda bg-superficie px-6 py-14 text-center shadow-[var(--shadow-cartao)]">
      <span className="flex h-12 w-12 items-center justify-center rounded-sistema bg-marca-fundo-2 text-marca">
        <Icone nome="payments" tamanho={26} />
      </span>

      <h2 className="font-titulo text-lg font-semibold text-tinta">O caixa está fechado</h2>

      <p className="max-w-[420px] text-[13.5px] leading-[1.6] text-texto-suave">
        Abra o turno contando o dinheiro que já está na gaveta. A partir daí, cada venda em
        espécie entra aqui sozinha, e o fechamento compara o que você contar com o que o sistema
        esperava.
      </p>

      {podeAbrir ? (
        <div className="mt-2">
            <Botao icone="payments" onClick={aoAbrir}>
              Abrir caixa
            </Botao>
          </div>
      ) : (
        <p className="mt-1 text-[12.5px] text-texto-fraco">
          Seu papel não abre caixa. Peça a um gerente.
        </p>
      )}
    </section>
  )
}

function horaDe(iso: string): string {
  const data = new Date(iso)
  return Number.isNaN(data.getTime())
    ? '—'
    : data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
}
